import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Templates page — Phase 2 frontend (Invoice end-to-end).
//
// Three sections stacked:
//
//   1. "Start a template"  — lists active template definitions
//      (Invoice is seeded automatically). Click → opens an inline
//      instance editor.
//
//   2. "Live runs"         — lists existing template_instances so you
//      can come back to one mid-flow. Click → opens the same editor
//      with the existing schema_values + asset pick.
//
//   3. "Template Library"  — lists template_assets (uploaded .docx /
//      .html), shows their detected merge fields and version. Upload
//      form at the top.
//
// The inline editor renders the picked template's schema as a form,
// PATCH's schema_values on edit, and a Render dropdown picks an
// asset and triggers POST /instances/{id}/render to stream back the
// merged file as a download.
//
// All of this is the minimum loop for proving the templates pipeline.
// Later phases add the always-on chat router, conversion chains,
// project-page integration, and per-flow step UIs.
import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, API_BASE } from "../api/client";
import { notify } from "../components/Toast";
import { useAuth } from "../hooks/useAuth";
const CATEGORY_OPTIONS = [
    { value: "contract", label: "Contract" },
    { value: "invoice", label: "Invoice" },
    { value: "email", label: "Email" },
];
const STATE_LABEL = {
    draft: "Draft",
    running: "Running",
    done: "Done",
    cancelled: "Cancelled",
};
export default function TemplatesPage() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [definitions, setDefinitions] = useState([]);
    const [assets, setAssets] = useState([]);
    const [instances, setInstances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeInstanceId, setActiveInstanceId] = useState(null);
    const [tab, setTab] = useState("run");
    // Inline "name this run" card: which definition is being started.
    const [startingDef, setStartingDef] = useState(null);
    const [startName, setStartName] = useState("");
    const [starting, setStarting] = useState(false);
    // Admin-only: which template definition is currently being edited
    // (null = none open). Null state hides the editor.
    const [editingDefId, setEditingDefId] = useState(null);
    // SOP→template draft (proposed by the LLM, not yet saved).
    const [sopDraft, setSopDraft] = useState(null);
    async function reload() {
        setLoading(true);
        try {
            const [defs, asts, insts] = await Promise.all([
                api.get("/templates/definitions"),
                api.get("/templates/assets"),
                api.get("/templates/instances"),
            ]);
            setDefinitions(defs);
            setAssets(asts);
            setInstances(insts);
        }
        catch (e) {
            notify.error("Couldn't load templates", e instanceof Error ? e.message : undefined);
        }
        finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        void reload();
    }, []);
    function openStart(def) {
        setStartingDef(def);
        setStartName(def.kind === "invoice"
            ? "Invoice for "
            : def.kind === "publishing_signee"
                ? "Signee: "
                : def.kind === "sales_acquisition"
                    ? "Prospect: "
                    : `${def.name} — `);
    }
    async function confirmStart() {
        if (!startingDef)
            return;
        const name = startName.trim();
        if (!name) {
            notify.error("Give this run a name");
            return;
        }
        setStarting(true);
        try {
            const inst = await api.post("/templates/instances", {
                template_kind: startingDef.kind,
                name,
                schema_values: {},
            });
            setStartingDef(null);
            setStartName("");
            await reload();
            setActiveInstanceId(inst.id);
            setTab("run");
        }
        catch (e) {
            notify.error("Couldn't start template", e instanceof Error ? e.message : undefined);
        }
        finally {
            setStarting(false);
        }
    }
    const activeRuns = instances.filter((i) => i.state !== "done" && i.state !== "cancelled");
    const finishedRuns = instances.filter((i) => i.state === "done" || i.state === "cancelled");
    return (_jsxs("div", { className: "page templates-page", children: [_jsxs("header", { className: "page-header tpl-page-header", children: [_jsxs("div", { children: [_jsx("h1", { children: "Templates" }), _jsx("p", { className: "muted small", style: { margin: 0 }, children: "Run a process, build a new one from an SOP, or manage document assets." })] }), _jsxs("nav", { className: "tpl-tabs", "aria-label": "Templates sections", children: [_jsxs("button", { type: "button", className: tab === "run" ? "active" : "", onClick: () => setTab("run"), children: ["Run", activeRuns.length > 0 && (_jsx("span", { className: "tpl-tab-count", children: activeRuns.length }))] }), isAdmin && (_jsx("button", { type: "button", className: tab === "build" ? "active" : "", onClick: () => setTab("build"), children: "Build" })), _jsxs("button", { type: "button", className: tab === "library" ? "active" : "", onClick: () => setTab("library"), children: ["Library", assets.length > 0 && (_jsx("span", { className: "tpl-tab-count", children: assets.length }))] })] })] }), loading && _jsx("p", { className: "muted", children: "Loading\u2026" }), tab === "run" && (_jsxs(_Fragment, { children: [_jsxs("section", { className: "templates-section", children: [_jsx("h2", { children: "Start a template" }), definitions.length === 0 && !loading && (_jsxs("div", { className: "tpl-empty", children: [_jsx("p", { children: "No templates yet." }), isAdmin ? (_jsx("button", { type: "button", className: "btn-primary", onClick: () => setTab("build"), children: "Build one from an SOP" })) : (_jsx("p", { className: "muted small", children: "Ask an admin to build one from an SOP." }))] })), _jsx("div", { className: "tpl-def-grid", children: definitions.map((d) => (_jsxs("article", { className: `tpl-def-card ${startingDef?.id === d.id ? "starting" : ""}`, children: [_jsxs("header", { children: [_jsx("h3", { children: d.name }), _jsx("code", { className: "tpl-def-kind", children: d.kind })] }), d.description && (_jsx("p", { className: "tpl-def-desc", children: d.description })), _jsxs("div", { className: "tpl-def-meta", children: [_jsxs("span", { children: [d.flow.length, " steps"] }), _jsxs("span", { children: [d.schema.filter((f) => f.required).length, " to start"] })] }), startingDef?.id === d.id ? (_jsxs("form", { className: "tpl-start-inline", onSubmit: (e) => {
                                                e.preventDefault();
                                                void confirmStart();
                                            }, children: [_jsx("input", { type: "text", value: startName, onChange: (e) => setStartName(e.target.value), placeholder: "Name this run", autoFocus: true, disabled: starting }), _jsxs("div", { className: "tpl-start-inline-actions", children: [_jsx("button", { type: "button", className: "btn-link", onClick: () => setStartingDef(null), disabled: starting, children: "Cancel" }), _jsx("button", { type: "submit", className: "btn-primary", disabled: starting || !startName.trim(), children: starting ? "Starting…" : "Start" })] })] })) : (_jsxs("div", { className: "tpl-def-actions", children: [_jsx("button", { type: "button", className: "btn-primary", onClick: () => openStart(d), children: "Start" }), isAdmin && (_jsx("button", { type: "button", className: "btn-link", onClick: () => {
                                                        setEditingDefId(d.id);
                                                        setTab("build");
                                                    }, children: "Edit" }))] }))] }, d.id))) })] }), _jsxs("section", { className: "templates-section", children: [_jsx("h2", { children: "Live runs" }), activeRuns.length === 0 && !loading && (_jsxs("div", { className: "tpl-empty", children: [_jsx("p", { children: "Nothing in progress." }), _jsx("p", { className: "muted small", children: "Start a template above to kick off a run." })] })), _jsx("div", { className: "tpl-inst-list", children: activeRuns.map((inst) => (_jsx(RunRow, { inst: inst, active: activeInstanceId === inst.id, onClick: () => setActiveInstanceId(activeInstanceId === inst.id ? null : inst.id) }, inst.id))) }), activeInstanceId && (_jsx(InstanceEditor, { instanceId: activeInstanceId, definitions: definitions, assets: assets, onClose: () => setActiveInstanceId(null), onUpdated: reload })), finishedRuns.length > 0 && (_jsxs("details", { className: "tpl-finished", children: [_jsxs("summary", { children: ["Finished (", finishedRuns.length, ")"] }), _jsx("div", { className: "tpl-inst-list", children: finishedRuns.map((inst) => (_jsx(RunRow, { inst: inst, active: activeInstanceId === inst.id, onClick: () => setActiveInstanceId(activeInstanceId === inst.id ? null : inst.id) }, inst.id))) })] }))] })] })), tab === "build" && isAdmin && (_jsxs(_Fragment, { children: [_jsx(SopGeneratorSection, { draft: sopDraft, setDraft: setSopDraft, onSaved: reload }), _jsxs("section", { className: "templates-section", children: [_jsx("h2", { children: "Edit templates" }), definitions.length === 0 && !loading && (_jsx("p", { className: "muted small", children: "No templates yet \u2014 generate one from an SOP above." })), _jsx("div", { className: "tpl-edit-list", children: definitions.map((d) => (_jsxs("button", { type: "button", className: `tpl-edit-row ${editingDefId === d.id ? "active" : ""}`, onClick: () => setEditingDefId(editingDefId === d.id ? null : d.id), children: [_jsx("span", { className: "tpl-inst-title", children: d.name }), _jsxs("span", { className: "tpl-inst-meta", children: [_jsx("code", { className: "tpl-def-kind", children: d.kind }), _jsxs("span", { className: "muted small", children: [d.flow.length, " steps \u00B7 ", d.schema.length, " fields"] })] })] }, d.id))) }), editingDefId && (_jsx(DefinitionEditor, { definition: definitions.find((d) => d.id === editingDefId) ?? null, assets: assets, onClose: () => setEditingDefId(null), onSaved: reload }))] })] })), tab === "library" && (_jsxs("section", { className: "templates-section", children: [_jsx("h2", { children: "Template Library" }), _jsxs("p", { className: "muted small", style: { marginTop: -8 }, children: ["Upload .docx / .html files. Merge fields like", " ", _jsx("code", { children: `{{field_name}}` }), " get scanned automatically and surfaced on the tile so you know what each template needs."] }), isAdmin && _jsx(AssetUploader, { onUploaded: reload }), _jsxs("div", { className: "tpl-asset-grid", children: [assets.length === 0 && !loading && (_jsxs("div", { className: "tpl-empty", children: [_jsx("p", { children: "Library is empty." }), _jsxs("p", { className: "muted small", children: ["Upload a .docx with ", _jsx("code", { children: `{{merge_fields}}` }), " to make it renderable from a template step."] })] })), assets.map((a) => (_jsxs("article", { className: `tpl-asset-card cat-${a.category}`, children: [_jsxs("header", { children: [_jsx("span", { className: "tpl-asset-ic", children: a.category === "invoice"
                                                    ? "🧾"
                                                    : a.category === "contract"
                                                        ? "📜"
                                                        : "✉" }), _jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsx("h3", { children: a.name }), _jsxs("span", { className: "muted small", children: [a.category, " \u00B7 ", a.file_type, " \u00B7 v", a.version, " \u00B7", " ", Math.round(a.file_size_bytes / 1024), " KB"] })] })] }), _jsxs("div", { className: "tpl-asset-fields", children: [_jsxs("div", { className: "tpl-asset-fields-h", children: ["Merge fields (", a.merge_fields.length, ")"] }), _jsxs("div", { children: [a.merge_fields.map((mf) => (_jsx("span", { className: "tpl-merge-pill", children: `{{${mf.key}}}` }, mf.key))), a.merge_fields.length === 0 && (_jsx("span", { className: "muted small", children: "No merge fields detected." }))] })] }), _jsx("div", { className: "tpl-asset-actions", children: _jsx("a", { href: `${API_BASE}/templates/assets/${a.id}/download`, target: "_blank", rel: "noopener noreferrer", className: "btn-link", children: "Download" }) })] }, a.id)))] })] }))] }));
}
/** One row in the Live runs list: name, template, progress "3/7 ·
 * Current step", state pill. */
function RunRow({ inst, active, onClick, }) {
    const total = inst.flow_step_count ?? 0;
    const done = (inst.step_state?.completed_step_orders ?? []).length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return (_jsxs("button", { type: "button", className: `tpl-inst-row ${active ? "active" : ""}`, onClick: onClick, children: [_jsxs("span", { className: "tpl-inst-main", children: [_jsx("span", { className: "tpl-inst-title", children: inst.name }), _jsxs("span", { className: "tpl-inst-sub muted small", children: [inst.template_name || inst.template_kind, total > 0 && (_jsxs(_Fragment, { children: [" · ", _jsxs("strong", { children: [done, "/", total] }), inst.current_step_name && inst.state !== "done" && (_jsxs(_Fragment, { children: [" \u00B7 ", inst.current_step_name] }))] }))] }), total > 0 && (_jsx("span", { className: "tpl-inst-bar", "aria-hidden": "true", children: _jsx("span", { style: { width: `${pct}%` } }) }))] }), _jsxs("span", { className: "tpl-inst-meta", children: [_jsx("span", { className: `tpl-inst-state state-${inst.state}`, children: STATE_LABEL[inst.state] ?? inst.state }), _jsx("span", { className: "muted small", children: new Date(inst.updated_at).toLocaleDateString() })] })] }));
}
// ── Upload form ────────────────────────────────────────────────────
function AssetUploader({ onUploaded }) {
    const fileRef = useRef(null);
    const [name, setName] = useState("");
    const [category, setCategory] = useState("invoice");
    const [busy, setBusy] = useState(false);
    async function submit(e) {
        e.preventDefault();
        const file = fileRef.current?.files?.[0];
        if (!file) {
            notify.error("Pick a file first");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("name", name || file.name);
        fd.append("category", category);
        setBusy(true);
        try {
            // FormData uploads can't go through the JSON-only api client —
            // hit fetch directly with the same cookies + relative path.
            const res = await fetch(`${API_BASE}/templates/assets`, {
                method: "POST",
                body: fd,
                credentials: "include",
            });
            if (!res.ok) {
                let detail = "";
                try {
                    const j = await res.json();
                    detail = typeof j?.detail === "string" ? j.detail : "";
                }
                catch {
                    /* ignore */
                }
                throw new ApiError(res.status, detail || res.statusText);
            }
            notify.success("Uploaded", name || file.name);
            setName("");
            if (fileRef.current)
                fileRef.current.value = "";
            onUploaded();
        }
        catch (e) {
            notify.error("Upload failed", e instanceof Error ? e.message : undefined);
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs("form", { className: "tpl-uploader", onSubmit: submit, children: [_jsx("input", { type: "file", ref: fileRef, accept: ".docx,.html,.md,.txt", disabled: busy }), _jsx("input", { type: "text", placeholder: "Display name (defaults to filename)", value: name, onChange: (e) => setName(e.target.value), disabled: busy }), _jsx("select", { value: category, onChange: (e) => setCategory(e.target.value), disabled: busy, children: CATEGORY_OPTIONS.map((o) => (_jsx("option", { value: o.value, children: o.label }, o.value))) }), _jsx("button", { type: "submit", className: "btn-primary", disabled: busy, children: busy ? "Uploading…" : "Upload" })] }));
}
// ── Instance editor ────────────────────────────────────────────────
function InstanceEditor({ instanceId, definitions, assets, onClose, onUpdated, }) {
    const [instance, setInstance] = useState(null);
    const [savingKey, setSavingKey] = useState(null);
    const [editingKey, setEditingKey] = useState(null);
    const [editingValue, setEditingValue] = useState("");
    // Chat thread state: the intake feels like a Claude conversation —
    // assistant opens, user replies, assistant extracts + asks the next
    // question, loop until everything's filled. Each turn round-trips
    // through /extract which now merges values directly into the
    // instance, so we just trust the returned reply + refresh.
    const [chatInput, setChatInput] = useState("");
    const [sending, setSending] = useState(false);
    const [messages, setMessages] = useState([]);
    const chatLogRef = useRef(null);
    async function load() {
        try {
            const inst = await api.get(`/templates/instances/${instanceId}`);
            setInstance(inst);
        }
        catch (e) {
            notify.error("Couldn't load instance", e instanceof Error ? e.message : undefined);
        }
    }
    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instanceId]);
    const definition = useMemo(() => definitions.find((d) => d.id === instance?.template_definition_id) ??
        null, [definitions, instance?.template_definition_id]);
    // On first load of an instance + its definition: fill any "today_*"
    // defaults and seed an opening assistant message so the user sees a
    // greeting like a Claude chat instead of an empty intake form.
    const seededRef = useRef(null);
    useEffect(() => {
        if (!instance || !definition)
            return;
        if (seededRef.current === instance.id)
            return;
        seededRef.current = instance.id;
        void (async () => {
            const next = await applyDefaults(instance, definition);
            if (next !== instance)
                setInstance(next);
            // Compose the opening message. If the instance already has
            // values (returning to an in-progress run), acknowledge that
            // and ask for the next required-but-empty field; otherwise open
            // with a fresh "tell me about this" prompt.
            const required = definition.schema.filter((f) => f.required);
            const stillEmpty = required
                .filter((f) => {
                const v = next.schema_values[f.key];
                if (v == null)
                    return true;
                if (typeof v === "string" && v.trim() === "")
                    return true;
                if (Array.isArray(v) && v.length === 0)
                    return true;
                return false;
            })
                .map((f) => (f.label ?? f.key).toLowerCase());
            const greeting = Object.keys(next.schema_values).length === 0
                ? `Hi — I'll help you fill out this ${definition.name} template. Tell me whatever you know about it. You can drop everything in one message, or I'll walk through it field by field.`
                : stillEmpty.length === 0
                    ? `Welcome back. Everything looks filled in — hit Render below whenever you're ready.`
                    : `Welcome back. Still need: ${stillEmpty.slice(0, 3).join(", ")}${stillEmpty.length > 3 ? ` and ${stillEmpty.length - 3} more` : ""}. What's the ${stillEmpty[0]}?`;
            setMessages([{ role: "assistant", text: greeting }]);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance?.id, definition?.id]);
    // Auto-scroll the chat log to the bottom on new messages.
    useEffect(() => {
        if (chatLogRef.current) {
            chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
        }
    }, [messages, sending]);
    async function sendMessage() {
        if (!instance || !chatInput.trim() || sending)
            return;
        const userText = chatInput.trim();
        // Append user message immediately so the thread feels live.
        setMessages((m) => [...m, { role: "user", text: userText }]);
        setChatInput("");
        setSending(true);
        try {
            const r = await api.post(`/templates/instances/${instance.id}/extract`, {
                chat_input: userText,
            });
            // Backend has merged + persisted; refetch the instance so the
            // "what we still need" list reflects the new state.
            const fresh = await api.get(`/templates/instances/${instance.id}`);
            setInstance(fresh);
            const reply = r.reply ||
                (Object.keys(r.extracted).length > 0
                    ? `Got it — saved ${Object.keys(r.extracted).length} field${Object.keys(r.extracted).length === 1 ? "" : "s"}.`
                    : "I couldn't pull anything new from that. Try a different angle?");
            setMessages((m) => [...m, { role: "assistant", text: reply }]);
            await onUpdated();
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            const friendly = /503/.test(msg)
                ? "AI chat isn't configured on the server yet. You can still fill the fields below by clicking them."
                : msg;
            setMessages((m) => [
                ...m,
                { role: "assistant", text: `Something went wrong: ${friendly}` },
            ]);
        }
        finally {
            setSending(false);
        }
    }
    async function commitInlineEdit(key) {
        if (!instance || editingKey !== key)
            return;
        setSavingKey(key);
        try {
            const next = await api.patch(`/templates/instances/${instance.id}`, { schema_values: { [key]: editingValue } });
            setInstance(next);
            setEditingKey(null);
            setEditingValue("");
            await onUpdated();
        }
        catch (e) {
            notify.error("Couldn't save", e instanceof Error ? e.message : undefined);
        }
        finally {
            setSavingKey(null);
        }
    }
    // Format today as "15th day of June, 2026" — the legal phrasing used
    // in the Publishing Signee agreement. The Invoice template's
    // issue_date wants YYYY-MM-DD instead, but the schema marks Invoice's
    // issue_date as "today" not "today_legal", so this helper is only
    // applied to fields whose schema default is "today_legal".
    function formatLegalDate(d) {
        const day = d.getDate();
        const month = d.toLocaleDateString("en-US", { month: "long" });
        const year = d.getFullYear();
        const suffix = day >= 11 && day <= 13
            ? "th"
            : day % 10 === 1
                ? "st"
                : day % 10 === 2
                    ? "nd"
                    : day % 10 === 3
                        ? "rd"
                        : "th";
        return `${day}${suffix} day of ${month}, ${year}`;
    }
    // Fill any schema defaults the user hasn't touched yet — primarily
    // effective_date on the Publishing Signee template, which should
    // default to the date the template is being filled out.
    async function applyDefaults(inst, defn) {
        const patches = {};
        for (const f of defn.schema) {
            const current = inst.schema_values[f.key];
            const empty = current == null ||
                (typeof current === "string" && current.trim() === "");
            if (!empty)
                continue;
            if (f.default === "today_legal") {
                patches[f.key] = formatLegalDate(new Date());
            }
            else if (f.default === "today" && f.type === "date") {
                patches[f.key] = new Date().toISOString().slice(0, 10);
            }
        }
        if (Object.keys(patches).length === 0)
            return inst;
        try {
            return await api.patch(`/templates/instances/${inst.id}`, { schema_values: patches });
        }
        catch {
            return inst;
        }
    }
    if (!instance)
        return null;
    const flowSteps = definition?.flow ?? [];
    const totalSteps = flowSteps.length;
    const doneSteps = (instance.step_state?.completed_step_orders ?? []).length;
    const currentOrder = instance.step_state?.current_step_order ?? 1;
    const currentStep = flowSteps.find((s) => s.order === currentOrder) ?? null;
    const progressPct = totalSteps > 0 ? Math.round((doneSteps / totalSteps) * 100) : 0;
    return (_jsxs("div", { className: "tpl-editor", children: [_jsxs("header", { className: "tpl-editor-head", children: [_jsxs("div", { className: "tpl-editor-head-main", children: [_jsx("h3", { children: instance.name }), _jsxs("span", { className: "muted small", children: [definition?.name ?? instance.template_kind, " \u00B7", " ", STATE_LABEL[instance.state] ?? instance.state] })] }), _jsx("button", { type: "button", className: "btn-link", onClick: onClose, style: { marginLeft: "auto" }, children: "Close \u00D7" })] }), totalSteps > 0 && (_jsxs("div", { className: "tpl-progress", children: [_jsxs("div", { className: "tpl-progress-text", children: [_jsxs("strong", { children: ["Step ", Math.min(currentOrder, totalSteps), " of ", totalSteps] }), currentStep && (_jsxs("span", { className: "muted", children: [" \u00B7 ", currentStep.name] })), _jsxs("span", { className: "tpl-progress-pct", children: [progressPct, "%"] })] }), _jsx("div", { className: "tpl-progress-bar", "aria-hidden": "true", children: _jsx("span", { style: { width: `${progressPct}%` } }) })] })), _jsxs("div", { className: "tpl-chat-thread", children: [_jsxs("div", { className: "tpl-chat-log", ref: chatLogRef, children: [messages.map((m, i) => (_jsx("div", { className: `tpl-chat-msg tpl-chat-msg-${m.role}`, children: _jsx("div", { className: "tpl-chat-bubble", children: m.text }) }, i))), sending && (_jsx("div", { className: "tpl-chat-msg tpl-chat-msg-assistant", children: _jsxs("div", { className: "tpl-chat-bubble tpl-chat-typing", children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {})] }) }))] }), _jsxs("form", { className: "tpl-chat-composer", onSubmit: (e) => {
                            e.preventDefault();
                            void sendMessage();
                        }, children: [_jsx("textarea", { value: chatInput, onChange: (e) => setChatInput(e.target.value), placeholder: "Reply...", rows: 1, disabled: sending, onKeyDown: (e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        void sendMessage();
                                    }
                                } }), _jsx("button", { type: "submit", className: "tpl-chat-send", disabled: sending || !chatInput.trim(), "aria-label": "Send", children: _jsx("svg", { viewBox: "0 0 24 24", width: "18", height: "18", fill: "none", stroke: "currentColor", strokeWidth: "2.4", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M5 12h14M13 6l6 6-6 6" }) }) })] })] }), _jsxs("div", { className: "tpl-needs", children: [!definition && (_jsx("p", { className: "muted small", children: "Template definition not loaded." })), definition && (() => {
                        // Step-aware split: a field is "needed now" if it's required,
                        // or the current step references it ({{key}} in email/body,
                        // or in the CRM mapping). Everything else collapses.
                        const cur = (flowSteps.find((s) => s.order === currentOrder) ??
                            null);
                        const refText = cur
                            ? [cur.body, cur.email_to, cur.email_subject, cur.email_body]
                                .filter(Boolean)
                                .join("\n")
                            : "";
                        const referenced = new Set();
                        for (const m of refText.matchAll(/\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g)) {
                            referenced.add(m[1]);
                        }
                        for (const v of Object.values(cur?.crm_field_mapping ?? {})) {
                            if (typeof v === "string")
                                referenced.add(v);
                        }
                        const nowFields = definition.schema.filter((f) => f.required || referenced.has(f.key));
                        const laterFields = definition.schema.filter((f) => !(f.required || referenced.has(f.key)));
                        const laterFilled = laterFields.filter((f) => {
                            const v = instance.schema_values[f.key];
                            return (v != null &&
                                !(typeof v === "string" && v.trim() === "") &&
                                !(Array.isArray(v) && v.length === 0));
                        }).length;
                        const renderNeedRow = (f) => {
                            const v = instance.schema_values[f.key];
                            const filled = v != null &&
                                !(typeof v === "string" && v.trim() === "") &&
                                !(Array.isArray(v) && v.length === 0);
                            const display = typeof v === "string" || typeof v === "number"
                                ? String(v)
                                : Array.isArray(v)
                                    ? `${v.length} item${v.length === 1 ? "" : "s"}`
                                    : v == null
                                        ? ""
                                        : JSON.stringify(v);
                            const isEditing = editingKey === f.key;
                            return (_jsxs("li", { className: `tpl-needs-row ${filled ? "filled" : "needed"}`, children: [_jsx("span", { className: "tpl-needs-mark", "aria-hidden": "true", children: filled ? "✓" : "○" }), _jsxs("div", { className: "tpl-needs-body", children: [_jsxs("div", { className: "tpl-needs-label", children: [f.label ?? f.key, f.required && !filled && (_jsxs("span", { className: "tpl-needs-required", children: [" ", "needed"] }))] }), isEditing ? (_jsxs("div", { className: "tpl-needs-edit", children: [f.type === "list" || f.type === "text" ? (_jsx("textarea", { value: editingValue, onChange: (e) => setEditingValue(e.target.value), rows: f.type === "list" ? 3 : 2, autoFocus: true })) : (_jsx("input", { type: f.type === "date"
                                                            ? "date"
                                                            : f.type === "number"
                                                                ? "number"
                                                                : "text", value: editingValue, onChange: (e) => setEditingValue(e.target.value), autoFocus: true })), _jsxs("div", { className: "tpl-needs-edit-actions", children: [_jsx("button", { type: "button", className: "btn-link", onClick: () => {
                                                                    setEditingKey(null);
                                                                    setEditingValue("");
                                                                }, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", onClick: () => void commitInlineEdit(f.key), disabled: savingKey === f.key, children: savingKey === f.key ? "Saving…" : "Save" })] })] })) : filled ? (_jsx("button", { type: "button", className: "tpl-needs-value", onClick: () => {
                                                    setEditingKey(f.key);
                                                    setEditingValue(display);
                                                }, title: "Click to edit", children: display })) : (_jsx("button", { type: "button", className: "tpl-needs-empty", onClick: () => {
                                                    setEditingKey(f.key);
                                                    setEditingValue("");
                                                }, children: "Click to fill, or just tell me in chat above" }))] })] }, f.key));
                        };
                        return (_jsxs(_Fragment, { children: [_jsxs("h4", { className: "tpl-needs-h", children: ["Needed for this step", _jsx("span", { className: "tpl-needs-count", children: nowFields.length })] }), _jsxs("ul", { className: "tpl-needs-list", children: [nowFields.length === 0 && (_jsx("li", { className: "muted small", style: { padding: "6px 10px" }, children: "Nothing specific \u2014 this step runs on its own." })), nowFields.map(renderNeedRow)] }), laterFields.length > 0 && (_jsxs("details", { className: "tpl-needs-later", children: [_jsxs("summary", { children: ["Later steps", _jsxs("span", { className: "tpl-needs-count", children: [laterFilled, "/", laterFields.length] })] }), _jsx("ul", { className: "tpl-needs-list", children: laterFields.map(renderNeedRow) })] }))] }));
                    })()] }), definition && (_jsx(StepViewer, { instance: instance, definition: definition, assets: assets, onInstanceChanged: load }))] }));
}
// Substitute {{key}} → schema_values[key] in body/email_to/email_subject
// text. Missing keys render as the placeholder so the user knows
// what's still empty.
function fillMergeFields(text, values) {
    return text.replace(/\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g, (m, key) => {
        const v = values[key];
        if (v == null)
            return m;
        if (typeof v === "string" || typeof v === "number")
            return String(v);
        return JSON.stringify(v);
    });
}
/** Step viewer that lives inside InstanceEditor — renders the
 * currently-selected flow step with the right UI for its kind:
 * email body (with merge + copy-to-clipboard), checklist (toggleable
 * + persisted), manual (markdown-ish text), doc (render asset), and
 * crm_create (preview + Create button that hits the step-state
 * endpoint, which fires the backend side effect). */
export function StepViewer({ instance, definition, assets, onInstanceChanged, }) {
    const [state, setState] = useState(null);
    const [pickedOrder, setPickedOrder] = useState(null);
    const [busy, setBusy] = useState(false);
    async function load() {
        try {
            const r = await api.get(`/templates/instances/${instance.id}/step-state`);
            setState(r);
            if (pickedOrder == null)
                setPickedOrder(r.current_step_order || 1);
        }
        catch {
            /* leave state null */
        }
    }
    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance.id]);
    const flow = definition.flow ?? [];
    const step = useMemo(() => flow.find((s) => s.order === pickedOrder) ?? flow[0] ?? null, [flow, pickedOrder]);
    const completedSet = new Set(state?.completed_step_orders ?? []);
    async function patchState(patch) {
        setBusy(true);
        try {
            const r = await api.patch(`/templates/instances/${instance.id}/step-state`, patch);
            setState(r);
            // Surface side effects as toasts (e.g. a CRM lead was created).
            for (const fx of r.side_effects ?? []) {
                if (fx.kind === "crm_create" && fx.created) {
                    notify.success("CRM lead created", fx.name ? `Lead: ${fx.name}` : undefined);
                }
                else if (fx.kind === "crm_create" && fx.error) {
                    notify.error("CRM lead failed", fx.error);
                }
            }
            await onInstanceChanged();
            return r;
        }
        catch (e) {
            notify.error("Couldn't update step", e instanceof Error ? e.message : undefined);
            return null;
        }
        finally {
            setBusy(false);
        }
    }
    async function markStepDone(order) {
        const done = new Set(completedSet);
        done.add(order);
        // Auto-advance current_step_order to the next non-completed step.
        const remaining = flow
            .map((s) => s.order)
            .filter((o) => typeof o === "number" && !done.has(o))
            .sort((a, b) => a - b);
        const next = remaining[0] ?? order;
        await patchState({
            completed_step_orders: Array.from(done),
            current_step_order: next,
        });
        setPickedOrder(next);
    }
    async function unmarkStepDone(order) {
        const done = new Set(completedSet);
        done.delete(order);
        await patchState({ completed_step_orders: Array.from(done) });
    }
    async function toggleChecklistItem(order, idx) {
        if (!state)
            return;
        const existing = state.checklists[String(order)] ?? [];
        // Seed from the definition if the instance hasn't touched this step yet.
        const baseStep = flow.find((s) => s.order === order);
        const seed = existing.length > 0
            ? existing.map((it) => ({ ...it }))
            : (baseStep?.checklist ?? []).map((it) => ({
                label: it.label,
                done: false,
            }));
        const next = seed.map((it, i) => i === idx ? { ...it, done: !it.done } : it);
        await patchState({
            checklists: { [String(order)]: next },
        });
    }
    if (!flow.length) {
        return (_jsx("div", { className: "tpl-stepper", children: _jsx("p", { className: "muted small", children: "No flow steps defined for this template. Click \"Edit steps\" on the template card to author them." }) }));
    }
    return (_jsxs("div", { className: "tpl-stepper", children: [_jsx("div", { className: "tpl-step-rail", children: flow.map((s) => {
                    const o = s.order;
                    const done = completedSet.has(o);
                    const isCurrent = pickedOrder === o;
                    return (_jsxs("button", { type: "button", className: `tpl-step-pill ${done ? "done" : ""} ${isCurrent ? "current" : ""}`, onClick: () => setPickedOrder(o), disabled: busy, title: s.name, children: [_jsx("span", { className: "tpl-step-num", children: done ? "✓" : o }), _jsx("span", { className: "tpl-step-pill-name", children: s.name }), _jsx("span", { className: "tpl-step-pill-kind", children: s.kind })] }, o));
                }) }), step && (_jsx(StepBody, { step: step, instance: instance, assets: assets, state: state, isDone: completedSet.has(step.order), busy: busy, onToggleChecklistItem: (idx) => void toggleChecklistItem(step.order, idx), onMarkDone: () => void markStepDone(step.order), onUnmarkDone: () => void unmarkStepDone(step.order) }))] }));
}
function StepBody({ step, instance, assets, state, isDone, busy, onToggleChecklistItem, onMarkDone, onUnmarkDone, }) {
    const values = instance.schema_values || {};
    // Compute the checklist to render: instance-state if present, else
    // the definition's seed list (with all items unchecked).
    const liveChecklist = useMemo(() => {
        const fromState = state?.checklists[String(step.order)];
        if (fromState && fromState.length > 0)
            return fromState;
        return (step.checklist ?? []).map((it) => ({
            label: it.label,
            done: false,
        }));
    }, [state, step]);
    async function copyEmail() {
        if (step.kind !== "email")
            return;
        const lines = [
            step.email_to ? `To: ${fillMergeFields(step.email_to, values)}` : "",
            step.email_subject
                ? `Subject: ${fillMergeFields(step.email_subject, values)}`
                : "",
            "",
            step.email_body ? fillMergeFields(step.email_body, values) : "",
        ]
            .filter((l) => l !== null)
            .join("\n");
        try {
            await navigator.clipboard.writeText(lines);
            notify.success("Email copied", "Paste it into your mail client.");
        }
        catch {
            notify.error("Couldn't copy to clipboard");
        }
    }
    function openMailto() {
        if (step.kind !== "email")
            return;
        const to = step.email_to ? fillMergeFields(step.email_to, values) : "";
        const subject = step.email_subject
            ? fillMergeFields(step.email_subject, values)
            : "";
        const body = step.email_body
            ? fillMergeFields(step.email_body, values)
            : "";
        const url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = url;
    }
    return (_jsxs("div", { className: `tpl-step-body kind-${step.kind} ${isDone ? "done" : ""}`, children: [_jsxs("header", { className: "tpl-step-h", children: [_jsxs("h4", { children: [_jsx("span", { className: "tpl-step-h-num", children: step.order }), step.name] }), _jsx("span", { className: "tpl-step-h-kind", children: step.kind })] }), step.description && (_jsx("p", { className: "tpl-step-desc", children: step.description })), step.body && (_jsx("div", { className: "tpl-step-prose", children: step.body.split("\n").map((line, i) => (_jsx("p", { children: line || " " }, i))) })), step.kind === "email" && (_jsxs("div", { className: "tpl-step-email", children: [_jsxs("div", { className: "tpl-step-email-meta", children: [_jsxs("span", { children: [_jsx("strong", { children: "To:" }), " ", step.email_to
                                        ? fillMergeFields(step.email_to, values)
                                        : "(no recipient set)"] }), _jsxs("span", { children: [_jsx("strong", { children: "Subject:" }), " ", step.email_subject
                                        ? fillMergeFields(step.email_subject, values)
                                        : "(no subject set)"] })] }), _jsx("pre", { className: "tpl-step-email-body", children: step.email_body
                            ? fillMergeFields(step.email_body, values)
                            : "(no body)" }), _jsxs("div", { className: "tpl-step-actions", children: [_jsx("button", { type: "button", className: "btn-link", onClick: () => void copyEmail(), children: "Copy" }), _jsx("button", { type: "button", className: "btn-primary", onClick: openMailto, children: "Open in mail client" })] })] })), step.kind === "checklist" && (_jsxs("ul", { className: "tpl-step-checklist", children: [liveChecklist.length === 0 && (_jsx("li", { className: "muted small", children: "No items." })), liveChecklist.map((it, i) => (_jsx("li", { className: `tpl-step-cl-row ${it.done ? "done" : ""}`, children: _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: !!it.done, onChange: () => onToggleChecklistItem(i), disabled: busy }), _jsx("span", { children: it.label })] }) }, i)))] })), step.kind === "resource_link" && step.resource_url && (_jsx("div", { className: "tpl-step-actions", children: _jsxs("a", { href: step.resource_url, target: "_blank", rel: "noopener noreferrer", className: "btn-primary", children: ["Open ", step.resource_label || "resource", " \u2197"] }) })), step.kind === "doc" && (_jsx(DocStepRender, { assetId: step.asset_id ?? null, assets: assets, instance: instance })), step.kind === "crm_create" && (_jsxs("div", { className: "tpl-step-crm", children: [_jsx("p", { className: "muted small", children: "Marking this step done will create a CRM lead from the following fields:" }), _jsx("ul", { className: "tpl-step-crm-preview", children: Object.entries(step.crm_field_mapping ?? {}).map(([role, key]) => (_jsxs("li", { children: [_jsxs("strong", { children: [role.replace("_field", ""), ":"] }), " ", String(values[key] ?? "—")] }, role))) }), instance.crm_lead_id && (_jsx("p", { className: "muted small", children: "CRM lead already linked to this run." }))] })), _jsx("div", { className: "tpl-step-mark", children: isDone ? (_jsx("button", { type: "button", className: "btn-link", onClick: onUnmarkDone, disabled: busy, children: "Mark not done" })) : (_jsx("button", { type: "button", className: "btn-primary", onClick: onMarkDone, disabled: busy, children: "Mark step done \u2192" })) })] }));
}
/** Doc step rendering: pick a Library asset + Render → download merged docx. */
function DocStepRender({ assetId, assets, instance, }) {
    const [picked, setPicked] = useState(assetId || "");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (!picked && assetId)
            setPicked(assetId);
        if (!picked && !assetId && assets.length > 0)
            setPicked(assets[0].id);
    }, [assetId, assets, picked]);
    async function render() {
        if (!picked) {
            notify.error("Pick a Library asset to render.");
            return;
        }
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/templates/instances/${instance.id}/render`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ asset_id: picked }),
                credentials: "include",
            });
            if (!res.ok) {
                let detail = res.statusText;
                try {
                    const j = await res.json();
                    detail = typeof j?.detail === "string" ? j.detail : detail;
                }
                catch {
                    /* ignore */
                }
                throw new Error(detail);
            }
            const blob = await res.blob();
            const cd = res.headers.get("Content-Disposition") || "";
            const m = /filename="([^"]+)"/.exec(cd);
            const filename = m?.[1] ?? `${instance.name}.docx`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            notify.success("Rendered", filename);
        }
        catch (e) {
            notify.error("Render failed", e instanceof Error ? e.message : undefined);
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs("div", { className: "tpl-step-doc", children: [_jsxs("select", { value: picked, onChange: (e) => setPicked(e.target.value), disabled: busy, children: [_jsx("option", { value: "", children: "\u2014 Pick a Library asset \u2014" }), assets.map((a) => (_jsxs("option", { value: a.id, children: [a.name, " (v", a.version, ")"] }, a.id)))] }), _jsx("button", { type: "button", className: "btn-primary", onClick: () => void render(), disabled: busy || !picked, children: busy ? "Rendering…" : "Render" })] }));
}
// ── SOP → template generator section (admin only) ───────────────────
function SopGeneratorSection({ draft, setDraft, onSaved, }) {
    const [tab, setTab] = useState("text");
    const [sopText, setSopText] = useState("");
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    async function generateFromText() {
        if (sopText.trim().length < 40) {
            notify.error("Paste more SOP text", "At least a few sentences.");
            return;
        }
        setBusy(true);
        try {
            const r = await api.post("/templates/definitions/generate-from-sop", {
                sop_text: sopText,
            });
            setDraft(r);
            notify.success("Draft ready", `${r.flow.length} steps, ${r.schema.length} fields`);
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            notify.error("Couldn't generate", /503/.test(msg)
                ? "AI isn't configured on the server yet."
                : msg);
        }
        finally {
            setBusy(false);
        }
    }
    async function generateFromFile() {
        const file = fileRef.current?.files?.[0];
        if (!file) {
            notify.error("Pick a file first");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await fetch(`${API_BASE}/templates/definitions/generate-from-sop-upload`, {
                method: "POST",
                body: fd,
                credentials: "include",
            });
            if (!res.ok) {
                let detail = res.statusText;
                try {
                    const j = await res.json();
                    detail = typeof j?.detail === "string" ? j.detail : detail;
                }
                catch {
                    /* ignore */
                }
                throw new Error(detail);
            }
            const r = (await res.json());
            setDraft(r);
            notify.success("Draft ready", `${r.flow.length} steps, ${r.schema.length} fields`);
        }
        catch (e) {
            notify.error("Couldn't generate", e instanceof Error ? e.message : String(e));
        }
        finally {
            setBusy(false);
        }
    }
    async function saveDraft(mode) {
        if (!draft)
            return;
        setSaving(true);
        try {
            if (mode === "overwrite") {
                const existing = await api.get("/templates/definitions");
                const dup = existing.find((d) => d.kind === draft.kind);
                if (!dup) {
                    notify.error("Nothing to overwrite", `No template with kind "${draft.kind}" exists — use Save as new.`);
                    return;
                }
                await api.patch(`/templates/definitions/${dup.id}`, {
                    name: draft.name,
                    description: draft.description,
                    schema: draft.schema,
                    flow: draft.flow,
                });
                notify.success("Updated", `${draft.name} overwritten`);
            }
            else {
                const created = await api.post("/templates/definitions", {
                    kind: draft.kind,
                    name: draft.name,
                    description: draft.description,
                    schema: draft.schema,
                    flow: draft.flow,
                });
                notify.success("Template created", `${created.name} is now in the Run tab`);
            }
            setDraft(null);
            setSopText("");
            await onSaved();
        }
        catch (e) {
            notify.error("Couldn't save draft", e instanceof Error ? e.message : undefined);
        }
        finally {
            setSaving(false);
        }
    }
    return (_jsxs("section", { className: "templates-section tpl-sop-section", children: [_jsx("h2", { children: "Build a template from an SOP" }), _jsxs("p", { className: "muted small", style: { marginTop: -8 }, children: ["Drop in a process doc (PDF, .docx, or pasted text) \u2014 Claude analyzes it and proposes the schema + flow steps. Review the draft, then apply it to an existing template. The generator prefers ", _jsx("code", { children: "crm_create" }), " and ", _jsx("code", { children: "email" }), " steps over manual checklists so ClockWork actually does the work."] }), _jsxs("div", { className: "tpl-sop-tabs", children: [_jsx("button", { type: "button", className: tab === "text" ? "active" : "", onClick: () => setTab("text"), children: "Paste text" }), _jsx("button", { type: "button", className: tab === "file" ? "active" : "", onClick: () => setTab("file"), children: "Upload file" })] }), tab === "text" ? (_jsxs("div", { className: "tpl-sop-text", children: [_jsx("textarea", { value: sopText, onChange: (e) => setSopText(e.target.value), placeholder: "Paste your SOP here\u2026", rows: 8, disabled: busy }), _jsx("div", { className: "tpl-sop-actions", children: _jsx("button", { type: "button", className: "btn-primary", onClick: () => void generateFromText(), disabled: busy || sopText.trim().length < 40, children: busy ? "Generating…" : "Generate draft" }) })] })) : (_jsxs("div", { className: "tpl-sop-file", children: [_jsx("input", { ref: fileRef, type: "file", accept: ".pdf,.docx,.txt,.md", disabled: busy }), _jsx("button", { type: "button", className: "btn-primary", onClick: () => void generateFromFile(), disabled: busy, children: busy ? "Generating…" : "Generate from file" })] })), draft && (_jsxs("div", { className: "tpl-sop-draft", children: [_jsxs("div", { className: "tpl-sop-draft-h", children: [_jsx("strong", { children: draft.name }), " ", _jsx("code", { className: "tpl-def-kind", children: draft.kind })] }), draft.description && (_jsx("p", { className: "tpl-def-desc", children: draft.description })), _jsxs("div", { className: "tpl-sop-draft-cols", children: [_jsxs("div", { children: [_jsxs("h5", { children: ["Fields (", draft.schema.length, ")"] }), _jsx("ul", { children: draft.schema.map((f, i) => (_jsxs("li", { children: [_jsx("strong", { children: f.label ?? f.key }), " ", _jsxs("span", { className: "muted small", children: [f.type, f.required ? " · required" : ""] })] }, i))) })] }), _jsxs("div", { children: [_jsxs("h5", { children: ["Steps (", draft.flow.length, ")"] }), _jsx("ol", { children: draft.flow.map((s) => (_jsxs("li", { children: [_jsx("strong", { children: s.name }), " ", _jsx("span", { className: "muted small", children: s.kind }), s.description && (_jsx("div", { className: "muted small", children: s.description }))] }, s.order))) })] })] }), _jsxs("div", { className: "tpl-sop-actions", children: [_jsx("button", { type: "button", className: "btn-link", onClick: () => setDraft(null), children: "Discard draft" }), _jsx("button", { type: "button", className: "btn-link", onClick: () => void saveDraft("overwrite"), disabled: saving, title: `Replace the existing "${draft.kind}" template with this draft`, children: "Overwrite existing" }), _jsx("button", { type: "button", className: "btn-primary", onClick: () => void saveDraft("new"), disabled: saving, children: saving ? "Saving…" : "Save as new template" })] })] }))] }));
}
// ── Admin: definition editor (edit name/description/flow steps) ─────
function DefinitionEditor({ definition, assets, onClose, onSaved, }) {
    const [name, setName] = useState(definition?.name ?? "");
    const [description, setDescription] = useState(definition?.description ?? "");
    const [flow, setFlow] = useState(definition?.flow ? JSON.parse(JSON.stringify(definition.flow)) : []);
    const [schema, setSchema] = useState(definition?.schema ? JSON.parse(JSON.stringify(definition.schema)) : []);
    const [saving, setSaving] = useState(false);
    const [section, setSection] = useState("steps");
    useEffect(() => {
        setName(definition?.name ?? "");
        setDescription(definition?.description ?? "");
        setFlow(definition?.flow ? JSON.parse(JSON.stringify(definition.flow)) : []);
        setSchema(definition?.schema
            ? JSON.parse(JSON.stringify(definition.schema))
            : []);
    }, [definition]);
    if (!definition)
        return null;
    function updateField(idx, patch) {
        setSchema((s) => s.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
    }
    function addField() {
        setSchema((s) => [
            ...s,
            {
                key: `field_${s.length + 1}`,
                label: "New field",
                type: "string",
                required: false,
                source: "chat",
            },
        ]);
    }
    function removeField(idx) {
        setSchema((s) => s.filter((_, i) => i !== idx));
    }
    function moveField(idx, dir) {
        setSchema((s) => {
            const next = [...s];
            const swap = idx + dir;
            if (swap < 0 || swap >= next.length)
                return s;
            [next[idx], next[swap]] = [next[swap], next[idx]];
            return next;
        });
    }
    function updateStep(idx, patch) {
        setFlow((f) => f.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    }
    function addStep() {
        const nextOrder = (flow[flow.length - 1]?.order ?? 0) + 1;
        setFlow((f) => [
            ...f,
            { order: nextOrder, kind: "manual", name: "New step", description: "" },
        ]);
    }
    function removeStep(idx) {
        setFlow((f) => f.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })));
    }
    function moveStep(idx, dir) {
        setFlow((f) => {
            const next = [...f];
            const swap = idx + dir;
            if (swap < 0 || swap >= next.length)
                return f;
            [next[idx], next[swap]] = [next[swap], next[idx]];
            return next.map((s, i) => ({ ...s, order: i + 1 }));
        });
    }
    async function save() {
        if (!definition)
            return;
        setSaving(true);
        try {
            await api.patch(`/templates/definitions/${definition.id}`, {
                name,
                description,
                flow,
                schema,
            });
            notify.success("Saved", `${name} updated`);
            await onSaved();
            onClose();
        }
        catch (e) {
            notify.error("Couldn't save", e instanceof Error ? e.message : undefined);
        }
        finally {
            setSaving(false);
        }
    }
    return (_jsxs("div", { className: "tpl-def-editor", children: [_jsxs("header", { className: "tpl-def-editor-h", children: [_jsxs("h3", { children: ["Edit template: ", definition.name] }), _jsx("button", { type: "button", className: "btn-link", onClick: onClose, children: "Close \u00D7" })] }), _jsxs("div", { className: "tpl-def-editor-body", children: [_jsxs("label", { className: "tpl-de-field", children: [_jsx("span", { children: "Name" }), _jsx("input", { type: "text", value: name, onChange: (e) => setName(e.target.value) })] }), _jsxs("label", { className: "tpl-de-field", children: [_jsx("span", { children: "Description" }), _jsx("textarea", { value: description, onChange: (e) => setDescription(e.target.value), rows: 2 })] }), _jsxs("div", { className: "tpl-de-sections", children: [_jsxs("button", { type: "button", className: section === "steps" ? "active" : "", onClick: () => setSection("steps"), children: ["Steps (", flow.length, ")"] }), _jsxs("button", { type: "button", className: section === "fields" ? "active" : "", onClick: () => setSection("fields"), children: ["Fields (", schema.length, ")"] })] }), section === "fields" && (_jsxs("div", { className: "tpl-de-fields", children: [_jsxs("p", { className: "muted small", style: { margin: "0 0 6px" }, children: ["Fields are what a run collects. Use the ", _jsx("code", { children: "key" }), " as a merge variable in emails + documents, e.g.", " ", _jsx("code", { children: `{{contact_email}}` }), "."] }), schema.map((f, i) => (_jsxs("div", { className: "tpl-de-field-row", children: [_jsx("input", { type: "text", value: f.key, onChange: (e) => updateField(i, {
                                            key: e.target.value
                                                .toLowerCase()
                                                .replace(/[^a-z0-9_]/g, "_"),
                                        }), placeholder: "key", className: "tpl-de-field-key" }), _jsx("input", { type: "text", value: f.label ?? "", onChange: (e) => updateField(i, { label: e.target.value }), placeholder: "Label", style: { flex: 1 } }), _jsxs("select", { value: f.type ?? "string", onChange: (e) => updateField(i, { type: e.target.value }), children: [_jsx("option", { value: "string", children: "text" }), _jsx("option", { value: "text", children: "long text" }), _jsx("option", { value: "number", children: "number" }), _jsx("option", { value: "date", children: "date" }), _jsx("option", { value: "list", children: "list" })] }), _jsxs("label", { className: "tpl-de-req", children: [_jsx("input", { type: "checkbox", checked: !!f.required, onChange: (e) => updateField(i, { required: e.target.checked }) }), "required"] }), _jsxs("div", { className: "tpl-de-step-actions", children: [i > 0 && (_jsx("button", { type: "button", className: "btn-link", onClick: () => moveField(i, -1), children: "\u2191" })), i < schema.length - 1 && (_jsx("button", { type: "button", className: "btn-link", onClick: () => moveField(i, 1), children: "\u2193" })), _jsx("button", { type: "button", className: "btn-link", onClick: () => removeField(i), children: "Remove" })] })] }, i))), _jsx("button", { type: "button", className: "btn-link", onClick: addField, children: "+ Add field" })] })), _jsxs("div", { className: "tpl-de-steps", style: { display: section === "steps" ? undefined : "none" }, children: [flow.map((s, i) => (_jsx(FlowStepEditor, { step: s, assets: assets, onChange: (patch) => updateStep(i, patch), onRemove: () => removeStep(i), onMoveUp: i > 0 ? () => moveStep(i, -1) : undefined, onMoveDown: i < flow.length - 1 ? () => moveStep(i, 1) : undefined }, i))), _jsx("button", { type: "button", className: "btn-link", onClick: addStep, children: "+ Add step" })] }), _jsxs("div", { className: "tpl-de-actions", children: [_jsx("button", { type: "button", className: "btn-link", onClick: onClose, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", onClick: () => void save(), disabled: saving, children: saving ? "Saving…" : "Save changes" })] })] })] }));
}
const STEP_KINDS = [
    { value: "manual", label: "Manual / instructions" },
    { value: "checklist", label: "Checklist" },
    { value: "email", label: "Email template" },
    { value: "doc", label: "Render document" },
    { value: "resource_link", label: "External resource link" },
    { value: "crm_create", label: "Create CRM lead" },
    { value: "wait", label: "Wait" },
];
function FlowStepEditor({ step, assets, onChange, onRemove, onMoveUp, onMoveDown, }) {
    return (_jsxs("div", { className: `tpl-de-step kind-${step.kind}`, children: [_jsxs("header", { children: [_jsx("span", { className: "tpl-de-step-num", children: step.order }), _jsx("select", { value: step.kind, onChange: (e) => onChange({ kind: e.target.value }), children: STEP_KINDS.map((k) => (_jsx("option", { value: k.value, children: k.label }, k.value))) }), _jsx("input", { type: "text", value: step.name, onChange: (e) => onChange({ name: e.target.value }), placeholder: "Step name", style: { flex: 1 } }), _jsxs("div", { className: "tpl-de-step-actions", children: [onMoveUp && (_jsx("button", { type: "button", className: "btn-link", onClick: onMoveUp, children: "\u2191" })), onMoveDown && (_jsx("button", { type: "button", className: "btn-link", onClick: onMoveDown, children: "\u2193" })), _jsx("button", { type: "button", className: "btn-link", onClick: onRemove, children: "Remove" })] })] }), _jsxs("label", { className: "tpl-de-field", children: [_jsx("span", { children: "One-line description" }), _jsx("input", { type: "text", value: step.description ?? "", onChange: (e) => onChange({ description: e.target.value }) })] }), _jsxs("label", { className: "tpl-de-field", children: [_jsx("span", { children: "Long-form instructions" }), _jsx("textarea", { value: step.body ?? "", onChange: (e) => onChange({ body: e.target.value }), rows: 3, placeholder: "What the runner should know or do at this step." })] }), step.kind === "email" && (_jsxs("div", { className: "tpl-de-kind-fields", children: [_jsxs("label", { className: "tpl-de-field", children: [_jsxs("span", { children: ["To (supports ", `{{field}}`, ")"] }), _jsx("input", { type: "text", value: step.email_to ?? "", onChange: (e) => onChange({ email_to: e.target.value }), placeholder: "{{contact_email}}" })] }), _jsxs("label", { className: "tpl-de-field", children: [_jsx("span", { children: "Subject" }), _jsx("input", { type: "text", value: step.email_subject ?? "", onChange: (e) => onChange({ email_subject: e.target.value }) })] }), _jsxs("label", { className: "tpl-de-field", children: [_jsx("span", { children: "Body" }), _jsx("textarea", { value: step.email_body ?? "", onChange: (e) => onChange({ email_body: e.target.value }), rows: 6, placeholder: "Hey {{primary_contact_name}}, \u2026" })] })] })), step.kind === "checklist" && (_jsx("div", { className: "tpl-de-kind-fields", children: _jsxs("label", { className: "tpl-de-field", children: [_jsx("span", { children: "Items (one per line)" }), _jsx("textarea", { value: (step.checklist ?? [])
                                .map((it) => it.label)
                                .join("\n"), onChange: (e) => onChange({
                                checklist: e.target.value
                                    .split("\n")
                                    .map((l) => l.trim())
                                    .filter(Boolean)
                                    .map((label) => ({ label })),
                            }), rows: 5 })] }) })), step.kind === "doc" && (_jsx("div", { className: "tpl-de-kind-fields", children: _jsxs("label", { className: "tpl-de-field", children: [_jsx("span", { children: "Bind a Library asset" }), _jsxs("select", { value: step.asset_id ?? "", onChange: (e) => onChange({ asset_id: e.target.value || null }), children: [_jsx("option", { value: "", children: "\u2014 Unbound (admin will bind later) \u2014" }), assets.map((a) => (_jsxs("option", { value: a.id, children: [a.name, " (v", a.version, ")"] }, a.id)))] })] }) })), step.kind === "resource_link" && (_jsxs("div", { className: "tpl-de-kind-fields", children: [_jsxs("label", { className: "tpl-de-field", children: [_jsx("span", { children: "Label" }), _jsx("input", { type: "text", value: step.resource_label ?? "", onChange: (e) => onChange({ resource_label: e.target.value }) })] }), _jsxs("label", { className: "tpl-de-field", children: [_jsx("span", { children: "URL" }), _jsx("input", { type: "text", value: step.resource_url ?? "", onChange: (e) => onChange({ resource_url: e.target.value }) })] })] })), step.kind === "crm_create" && (_jsxs("div", { className: "tpl-de-kind-fields", children: [_jsx("p", { className: "muted small", children: "Map schema field keys to the CRM lead row. Marking this step done creates the lead automatically." }), ["name_field", "email_field", "phone_field", "notes_field"].map((k) => (_jsxs("label", { className: "tpl-de-field", children: [_jsx("span", { children: k.replace("_field", "") }), _jsx("input", { type: "text", value: (step.crm_field_mapping ?? {})[k] ?? "", onChange: (e) => onChange({
                                    crm_field_mapping: {
                                        ...(step.crm_field_mapping ?? {}),
                                        [k]: e.target.value,
                                    },
                                }), placeholder: k === "name_field"
                                    ? "client_name"
                                    : k === "email_field"
                                        ? "contact_email"
                                        : k === "phone_field"
                                            ? "contact_phone"
                                            : "discovery_notes" })] }, k)))] }))] }));
}
